import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@material-ui/core";
import PrintIcon from "@material-ui/icons/Print";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import { authGet, authPut } from "../../lib";
import AdminNotesCard from "./Components/AdminNotesCard";

/**
 * One return or exchange — a port of returns_details.php / returns_details.tpl.
 *
 * Two note trails, deliberately kept apart: the status history is what the
 * customer was told and when, appended by the server on every status change;
 * the admin notes below are internal and never leave the building.
 */

const STATUS = {
  "": { label: "Pending", color: "#0d47a1" },
  AUTHORIZED: { label: "Authorized", color: "#1b5e20" },
  DECLINED: { label: "Declined", color: "#b71c1c" },
  CANCELLED: { label: "Cancelled", color: "#8d6e4f" },
  COMPLETED: { label: "Completed", color: "#6a1b9a" },
};

const CHOICES = [
  { value: "DECLINED", label: "Deny" },
  { value: "AUTHORIZED", label: "Authorize" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
];

const useStyles = makeStyles((theme) => ({
  page: { maxWidth: 1360, margin: "24px auto 0", width: "100%" },
  head: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  title: { fontSize: 17, fontWeight: 500 },
  spacer: { flex: 1 },
  stamp: { color: theme.palette.text.secondary, fontSize: 13 },
  badge: {
    padding: "2px 8px",
    borderRadius: 3,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: ".04em",
    color: "#fff",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  half: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  panel: { padding: theme.spacing(2) },
  wide: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  panelTitle: {
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: ".02em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.25),
  },
  line: { fontSize: 13, lineHeight: 1.85 },
  label: { color: theme.palette.text.secondary },
  muted: { color: theme.palette.text.secondary },
  link: { color: theme.palette.primary.main, textDecoration: "none", "&:hover": { textDecoration: "underline" } },
  special: { color: "#c00" },
  fromCustomer: { fontSize: 13, lineHeight: 1.7, whiteSpace: "pre-wrap" },
  radios: { flexDirection: "row", marginTop: theme.spacing(0.5) },
  actions: { display: "flex", gap: theme.spacing(1), marginTop: theme.spacing(1) },
  history: {
    maxHeight: 200,
    overflowY: "auto",
    background: "rgba(0,0,0,0.025)",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 3,
    padding: theme.spacing(1.25),
    fontSize: 12.5,
    lineHeight: 1.7,
  },
  entry: { marginBottom: theme.spacing(1), "&:last-child": { marginBottom: 0 } },
  entryHead: { color: theme.palette.text.secondary, fontWeight: 600 },
  entryPart: { marginLeft: theme.spacing(1.25), display: "block" },
  notice: {
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(1.5),
    borderRadius: 3,
    fontSize: 13,
    background: "rgba(27,94,32,0.10)",
    color: "#1b5e20",
  },
  error: { color: "#c62828", fontSize: 13, marginTop: theme.spacing(1) },
  loading: { padding: theme.spacing(6), textAlign: "center" },
}));

const money = (n) =>
  Number(n || 0).toLocaleString(undefined, { style: "currency", currency: "USD" });

const when = (s) => {
  if (!s) return "";
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(s) : d.toLocaleString();
};

/**
 * The log blob, as entries.
 *
 * Stored as "----<stamp> - <user>----Status: X|Comments: Y|" repeated. The
 * legacy screen unpicked it with four regexes in the template; this does the
 * same split once, in one place.
 */
function parseLog(raw) {
  if (!String(raw || "").trim()) return [];
  return String(raw)
    .split("----")
    .reduce((acc, chunk, i, all) => {
      // Stamps sit at odd indexes: ["", stamp, body, stamp, body, ...]
      if (i % 2 === 1) {
        acc.push({ head: chunk.trim(), parts: (all[i + 1] ?? "").split("|").map((p) => p.trim()).filter(Boolean) });
      }
      return acc;
    }, [])
    .reverse();
}

export function ReturnDetailPage() {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [comments, setComments] = useState("");

  const load = useCallback(async () => {
    setError("");
    const { data: res, error: err } = await authGet(`/returns/${id}`);
    if (err) {
      setError(err.message || "Could not load that return.");
      return;
    }
    setData(res);
    setNotes(res.notes ?? []);
    setStatus(res.return?.status ?? "");
    setComments("");
  }, [id]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  const save = async () => {
    if (!status) {
      setError("Choose a status first.");
      return;
    }
    setBusy(true);
    setError("");
    setNotice("");
    const { data: res, error: err } = await authPut(`/returns/${id}/status`, { status, comments });
    setBusy(false);

    if (err) {
      setError(err.message || "Could not update.");
      return;
    }
    setNotice(
      res.orderMovedToReturned
        ? `Updated — ${STATUS[res.status].label}. Order ${res.orderMovedToReturned} moved to Returned.`
        : `Updated — ${STATUS[res.status].label}.`,
    );
    load();
  };

  if (error && !data) {
    return (
      <Box className={classes.page}>
        <div className={classes.error}>{error}</div>
      </Box>
    );
  }
  if (!data) {
    return (
      <Box className={classes.page}>
        <div className={classes.loading}>
          <CircularProgress size={24} />
        </div>
      </Box>
    );
  }

  const r = data.return;
  const state = STATUS[r.status ?? ""] ?? STATUS[""];
  const log = parseLog(data.log);
  const go = (target) => () => history.push(`/returns/${target}`);

  /*
   * Amazon orders, and PayPal orders with no billing city, arrive with the
   * billing block unfilled — those show the shipping address instead, exactly
   * as the legacy template did.
   */
  const useShipping =
    r.pay_method === "Amazon" ||
    (r.pay_method === "PayPal" && !String(r.bill_city || "").trim());
  const addr = useShipping
    ? { a1: r.ship_address1, a2: r.ship_address2, city: r.ship_city, state: r.ship_state, zip: r.ship_zip }
    : { a1: r.bill_address1, a2: r.bill_address2, city: r.bill_city, state: r.bill_state, zip: r.bill_zip };

  return (
    <Box className={`${classes.page} order-print`}>
      <div className={classes.head}>
        <Button size="small" variant="outlined" className="no-print"
          onClick={() => history.push("/returns")}>
          ← Returns
        </Button>
        <Button size="small" variant="outlined" className="no-print"
          disabled={!data.prevId} onClick={go(data.prevId)}>
          ‹ Prev
        </Button>
        <Button size="small" variant="outlined" className="no-print"
          disabled={!data.nextId} onClick={go(data.nextId)}>
          Next ›
        </Button>

        <Typography className={classes.title}>
          RMA #: {Number(r.preowned) ? "P" : ""}{r.returns_id}
        </Typography>
        <span className={classes.badge} style={{ background: state.color }}>
          {state.label.toUpperCase()}
        </span>
        {r.request && (
          <span className={classes.badge} style={{ background: "#37474f" }}>{r.request}</span>
        )}

        <div className={classes.spacer} />
        <span className={classes.stamp}>{when(r.created)}</span>
        <IconButton size="small" className="no-print" title="Print this return"
          onClick={() => window.print()}>
          <PrintIcon fontSize="small" />
        </IconButton>
      </div>

      {notice && <div className={classes.notice}>{notice}</div>}

      <div className={classes.grid}>
        <Paper className={classes.panel}>
          <div className={classes.panelTitle}>Request</div>
          <div className={classes.line}>
            <span className={classes.label}>Submitted:</span> {when(r.created)}
          </div>
          <div className={classes.line}>
            <span className={classes.label}>Purchased:</span>{" "}
            {r.order_time ? when(r.order_time) : <span className={classes.muted}>—</span>}
          </div>
          <div className={classes.line}>
            <span className={classes.label}>Order #:</span>{" "}
            {r.order_id ? (
              <span className={classes.link} style={{ cursor: "pointer" }}
                onClick={() => history.push(`/orders/${r.order_id}`)}>
                {Number(r.is_preowned) ? "P" : ""}{r.order_id}
              </span>
            ) : (
              <span className={classes.muted}>{r.invoice || "—"}</span>
            )}
          </div>
          <div className={classes.line} style={{ marginTop: 8 }}>
            {r.title || [r.brand, r.model].filter(Boolean).join(" ")}
            {r.price != null && ` — ${money(r.price)}`}
            {!!Number(r.price_on_special) && (
              <span className={classes.special}> · On Special!</span>
            )}
          </div>
        </Paper>

        <Paper className={classes.panel}>
          <div className={classes.panelTitle}>
            {r.request ? `${r.request.charAt(0)}${r.request.slice(1).toLowerCase()}` : "Request"}
          </div>
          <div className={classes.label} style={{ fontSize: 12.5, marginBottom: 4 }}>
            From customer:
          </div>
          <div className={classes.fromCustomer}>{r.description}</div>
        </Paper>

        <Paper className={classes.panel}>
          <div className={classes.panelTitle}>Customer</div>
          <div className={classes.line}>
            {[r.bill_first_name === "x" ? "" : r.bill_first_name, r.bill_mi, r.bill_last_name]
              .filter(Boolean)
              .join(" ") || [r.first_name, r.last_name].filter(Boolean).join(" ")}
          </div>
          {r.bill_business_name && <div className={classes.line}>{r.bill_business_name}</div>}
          <div className={classes.line}>{addr.a1}</div>
          {addr.a2 && <div className={classes.line}>{addr.a2}</div>}
          <div className={classes.line}>
            {addr.city}
            {addr.city && addr.state ? ", " : ""}
            {addr.state} {addr.zip}
          </div>
          {r.bill_country && <div className={classes.line}>{r.bill_country}</div>}
          {r.home_phone && <div className={classes.line}>{r.home_phone}</div>}
          {r.contact_phone && <div className={classes.line}>{r.contact_phone}</div>}
          <div className={classes.line}>
            <a href={`mailto:${r.email}`}>{r.email}</a>
          </div>
          {r.pay_method_sh && (
            <div className={classes.line}>
              <span className={classes.label}>Paid by:</span> {r.pay_method_sh}
            </div>
          )}
        </Paper>
      </div>

      <div className={classes.half}>
        <Paper className={classes.panel}>
          <div className={classes.panelTitle}>Status Update</div>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Comment to the customer…"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
          />
          <RadioGroup
            className={classes.radios}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {CHOICES.map((c) => (
              <FormControlLabel
                key={c.value}
                value={c.value}
                control={<Radio color="primary" size="small" />}
                label={c.label}
              />
            ))}
          </RadioGroup>
          <div className={`${classes.actions} no-print`}>
            <Button size="small" variant="contained" color="primary" disabled={busy} onClick={save}>
              {busy ? "Saving…" : "Update"}
            </Button>
          </div>
          {status === "COMPLETED" && r.order_id && (
            <div className={classes.muted} style={{ fontSize: 12.5, marginTop: 8 }}>
              Completing this also moves order {r.order_id} to Returned.
            </div>
          )}
          {error && <div className={classes.error}>{error}</div>}
        </Paper>

        <Paper className={classes.panel}>
          <div className={classes.panelTitle}>Status History</div>
          <div className={classes.history}>
            {!log.length ? (
              <span className={classes.muted}>Nothing recorded yet.</span>
            ) : (
              log.map((e, i) => (
                <div className={classes.entry} key={`${e.head}-${i}`}>
                  <span className={classes.entryHead}>{e.head}</span>
                  {e.parts.map((p, n) => (
                    <span className={classes.entryPart} key={n}>{p}</span>
                  ))}
                </div>
              ))
            )}
          </div>
        </Paper>
      </div>

      <AdminNotesCard endpoint={`/returns/${id}`} notes={notes} onChange={setNotes} />
    </Box>
  );
}

export default ReturnDetailPage;
